import React, { useEffect, useState } from "react";
import axios from "axios";

import FrontendLayout from "../../../components/frontend/FrontendLayout";
import UserCommon from "../../../components/frontend/UserCommon";
import UserTabs from "../../../components/frontend/UserTabs";
import FormatIndianPrice from "../../../components/frontend/FormatIndianPrice";
import { formatShortPrice } from "../../../utils/formatNumber";
import { formatNumberIndian } from "../../../utils/formatNumber";
import { handleApiError } from "../../../utils/apiHelpers";
import { ToastContainer, toast } from "react-toastify";
import { useUserProfile } from "../../../hooks/useUserProfile";
import { useNavigate, useLocation, Link } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import NoResults from "../../../components/admin/common/NoResults";

// Add these utility functions near the top, after the imports
const trackLandViewInSession = (landId: number): boolean => {
  const today = new Date().toDateString();
  const sessionKey = `landViews_${today}`;

  try {
    const todayViews = JSON.parse(sessionStorage.getItem(sessionKey) || "{}");

    if (todayViews[landId]) {
      return false;
    }

    todayViews[landId] = true;
    sessionStorage.setItem(sessionKey, JSON.stringify(todayViews));

    return true;
  } catch (error) {
    console.error("Error tracking land view in session:", error);
    return false;
  }
};

const getSessionViewsCount = (landId: number): number => {
  const today = new Date().toDateString();
  const sessionKey = `landViews_${today}`;

  try {
    const todayViews = JSON.parse(sessionStorage.getItem(sessionKey) || "{}");
    return todayViews[landId] ? 1 : 0;
  } catch (error) {
    console.error("Error reading session views:", error);
    return 0;
  }
};

const getTotalSessionViewsCount = (): number => {
  const today = new Date().toDateString();
  const sessionKey = `landViews_${today}`;

  try {
    const todayViews = JSON.parse(sessionStorage.getItem(sessionKey) || "{}");
    return Object.keys(todayViews).length;
  } catch (error) {
    console.error("Error reading total session views:", error);
    return 0;
  }
};

const API_BASE = process.env.REACT_APP_API_BASE_URL;

interface Land {
  ln_id: number;
  ln_locality: string;
  ln_address: string;
  ln_total_area_sqft: number;
  ln_total_sqft_area: number;
  ln_plottype: string;
  ln_post_date: string;
  ln_price: number;
  ln_availability?: string;
  ln_image?: string;
  image_files?: string[];
  enquiry_count?: number;
  wishlist_count?: number;
  view_count?: number;
}

interface Pagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface LandCount {
  ln_id: number;
  ln_title: string;
  enquiry_count: number;
  wishlist_count: number;
  view_count: number;
}

const ITEMS_PER_PAGE = 8;

const MyLands: React.FC = () => {
  const [lands, setLands] = useState<Land[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [landCounts, setLandCounts] = useState<LandCount[]>([]);
  const [updatingAvailability, setUpdatingAvailability] = useState<
    number | null
  >(null);
  const [loadingAvailability, setLoadingAvailability] = useState<boolean>(true);

  const navigate = useNavigate();
  const location = useLocation();
  const token = localStorage.getItem("frontendUserToken");
  const userType = localStorage.getItem("frontendUserType");
  const userId = localStorage.getItem("frontendUserId");

  const profile = useUserProfile(token);

  const fetchLands = async (page = 1, status = "All") => {
    try {
      let statusQuery = "";
      if (status === "Sold_Out") {
        statusQuery = "&landAvail=Sold";
      } else if (status !== "All") {
        statusQuery = `&status=${status.toLowerCase()}`;
      }
      const userQuery = userId ? `&userId=${userId}` : "";

      const res = await axios.get(
        `${API_BASE}/api/landadd/list?page=${page}&limit=${ITEMS_PER_PAGE}${statusQuery}${userQuery}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (res.data.success) {
        const landsData = res.data.landadds || [];

        // Fetch availability status for each land
        const landsWithAvailability = await Promise.all(
          landsData.map(async (land: Land) => {
            try {
              const availabilityRes = await axios.get(
                `${API_BASE}/api/landadd/availability-status/${land.ln_id}`,
                {
                  headers: { Authorization: `Bearer ${token}` },
                }
              );

              if (availabilityRes.data.success) {
                return {
                  ...land,
                  ln_availability: availabilityRes.data.availability,
                };
              }
            } catch (err) {
              console.error(
                `Error fetching availability for land ${land.ln_id}:`,
                err
              );
            }

            // Default to "Avail" if there's an error
            return {
              ...land,
              ln_availability: "Avail",
            };
          })
        );

        setLands(landsWithAvailability);
        setPagination(res.data.pagination);
      } else {
        console.error("Failed to fetch lands:", res.data.message);
      }
    } catch (err) {
      handleApiError(err, navigate);
    } finally {
      setLoadingAvailability(false);
    }
  };

  const fetchLandCounts = async () => {
    if (!userId || !token) return;
    try {
      const res = await axios.get(`${API_BASE}/api/enquiry/counts/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        setLandCounts(res.data.counts || []);
      } else {
        console.error("Failed to fetch land counts:", res.data.message);
      }
    } catch (err) {
      console.error("Error fetching land counts:", err);
    }
  };

  // Function to update land availability
  const updateLandAvailability = async (
    landId: number,
    currentAvailability: string
  ) => {
    if (!token) return;

    setUpdatingAvailability(landId);
    try {
      const newAvailability = currentAvailability === "Sold" ? "Avail" : "Sold";

      const res = await axios.put(
        `${API_BASE}/api/landadd/update-availability/${landId}`,
        { availability: newAvailability },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (res.data.success) {
        toast.dismiss();
        toast.success(res.data.message);
        // Update the local state to reflect the change
        setLands((prevLands) =>
          prevLands.map((land) =>
            land.ln_id === landId
              ? { ...land, ln_availability: newAvailability }
              : land
          )
        );
      } else {
        toast.error("Failed to update availability");
      }
    } catch (err) {
      console.error("Error updating availability:", err);
      toast.error("Failed to update availability");
    } finally {
      setUpdatingAvailability(null);
    }
  };

  useEffect(() => {
    if (userType !== "1" && userType !== "2") {
      navigate("/403");
      return;
    }
    fetchLands(currentPage, filterStatus);
    fetchLandCounts();
  }, [currentPage, filterStatus, userType, navigate, userId, token]);

  useEffect(() => {
    if (location.state && (location.state as any).message) {
      toast.success((location.state as any).message);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const getCombinedViewCount = (
    land: Land,
    countData: LandCount | undefined
  ): number => {
    const dbViews = countData ? countData.view_count : 0;
    const sessionViews = getSessionViewsCount(land.ln_id);
    return dbViews + sessionViews;
  };

  // Merge land counts with lands data
  const enrichedLands = lands.map((land) => {
    const countData = landCounts.find((count) => count.ln_id === land.ln_id);
    const combinedViewCount = getCombinedViewCount(land, countData);

    return {
      ...land,
      enquiry_count: countData ? countData.enquiry_count : 0,
      wishlist_count: countData ? countData.wishlist_count : 0,
      view_count: combinedViewCount, // Use combined view count
    };
  });

  const filteredLands = enrichedLands.filter(
    (land) =>
      (land.ln_locality?.toLowerCase() || "").includes(
        searchTerm.toLowerCase()
      ) ||
      (land.ln_address?.toLowerCase() || "").includes(
        searchTerm.toLowerCase()
      ) ||
      (land.ln_plottype?.toLowerCase() || "").includes(searchTerm.toLowerCase())
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleFilterChange = (status: string) => {
    setFilterStatus(status);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage: number) => {
    if (pagination && newPage >= 1 && newPage <= pagination.totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Function to generate pagination items
  const renderPaginationItems = () => {
    if (!pagination) return null;

    const totalPages = pagination.totalPages;
    const current = currentPage;
    const items = [];

    // Always show first page
    items.push(
      <li key={1} className={`page-item ${current === 1 ? "active" : ""}`}>
        <a
          className="page-link"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            handlePageChange(1);
          }}
        >
          1
        </a>
      </li>
    );

    // Calculate start and end pages
    let startPage = Math.max(2, current - 2);
    let endPage = Math.min(totalPages - 1, current + 2);

    // Add ellipsis if needed after first page
    if (startPage > 2) {
      items.push(
        <li key="ellipsis1" className="page-item disabled">
          <span className="page-link">...</span>
        </li>
      );
    }

    // Add page numbers
    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <li key={i} className={`page-item ${current === i ? "active" : ""}`}>
          <a
            className="page-link"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              handlePageChange(i);
            }}
          >
            {i}
          </a>
        </li>
      );
    }

    // Add ellipsis if needed before last page
    if (endPage < totalPages - 1) {
      items.push(
        <li key="ellipsis2" className="page-item disabled">
          <span className="page-link">...</span>
        </li>
      );
    }

    // Always show last page if there is more than one page
    if (totalPages > 1) {
      items.push(
        <li
          key={totalPages}
          className={`page-item ${current === totalPages ? "active" : ""}`}
        >
          <a
            className="page-link"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              handlePageChange(totalPages);
            }}
          >
            {totalPages}
          </a>
        </li>
      );
    }

    return items;
  };

  return (
    <FrontendLayout>
      <ToastContainer />

      {/* Added the pagination styles */}
      <style>
        {`
          .pagination.circle .page-link {
            border-radius: 50% !important;
            width: 45px; height: 45px;
            line-height: 30px;
            text-align: center;
            margin: 0 4px;
            background: #f8f9fa;
            color: #824b76;
            transition: all 0.3s ease;
          }
          .pagination.circle .page-link:hover {
            background: #824b76;
            color: #fff;
          }
          .pagination.circle .page-item.active .page-link {
            background: #824b76;
            border-color: #824b76;
            color: #fff;
            font-weight: bold;
          }
        `}
      </style>

      <div className="user">
        <div className="tf-container py-5 user">
          <UserCommon
            name={profile.name}
            email={profile.email}
            imageUrl={profile.imageUrl}
            userType={profile?.userType}
          />

          <div className="tab" role="tabpanel">
            <UserTabs />

            <div className="tab-content tabs">
              <div role="tabpanel" className="tab-pane in active">
                <div className="row">
                  <div className="col-lg-8 pb-2 d-md-flex align-items-md-center">
                    <a
                      href="#"
                      className={`btn ${
                        filterStatus === "All"
                          ? "btn-primary"
                          : "btn-outline-primary"
                      } me-2 mb-2`}
                      onClick={(e) => {
                        e.preventDefault();
                        handleFilterChange("All");
                      }}
                    >
                      All
                    </a>
                    <a
                      href="#"
                      className={`btn ${
                        filterStatus === "Pending"
                          ? "btn-primary"
                          : "btn-outline-primary"
                      } me-2 mb-2`}
                      onClick={(e) => {
                        e.preventDefault();
                        handleFilterChange("Pending");
                      }}
                    >
                      Pending
                    </a>
                    <a
                      href="#"
                      className={`btn ${
                        filterStatus === "Approved"
                          ? "btn-primary"
                          : "btn-outline-primary"
                      } me-2 mb-2`}
                      onClick={(e) => {
                        e.preventDefault();
                        handleFilterChange("Approved");
                      }}
                    >
                      Approved
                    </a>
                    <a
                      href="#"
                      className={`btn ${
                        filterStatus === "Rejected"
                          ? "btn-primary"
                          : "btn-outline-primary"
                      } me-2 mb-2`}
                      onClick={(e) => {
                        e.preventDefault();
                        handleFilterChange("Rejected");
                      }}
                    >
                      Rejected
                    </a>
                    <a
                      href="#"
                      className={`btn ${
                        filterStatus === "Sold_Out"
                          ? "btn-primary"
                          : "btn-outline-primary"
                      } me-2 mb-2`}
                      onClick={(e) => {
                        e.preventDefault();
                        handleFilterChange("Sold_Out");
                      }}
                    >
                      Sold Out
                    </a>
                  </div>
                  <div className="col-lg-4">
                    <div className="input-group form">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search by locality, address, or Plot type..."
                        aria-label="Search"
                        value={searchTerm}
                        onChange={handleSearchChange}
                      />
                      <button
                        className="btn btn-primary btn-search"
                        type="button"
                      >
                        <i className="bi bi-search"></i>
                      </button>
                    </div>
                  </div>
                  <div className="col-lg-12 pb-5">
                    <hr />
                  </div>

                  {loadingAvailability ? (
                    <div className="col-lg-12 text-center">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : filteredLands.length > 0 ? (
                    filteredLands.map((land, index) => (
                      <div className="col-lg-3 pb-5" key={land.ln_id}>
                        <div className="box-location hover-img">
                          <div className="plot-location">
                            <i className="bi bi-geo-alt me-1"></i>
                            <div className="plot-location-text ms-2 text-16">
                              {land.ln_locality},<br /> {land.ln_address}
                            </div>
                          </div>
                          <div
                            className={`plot-details3 sec${(index % 5) + 1}`}
                          >
                            <div className="plot-details-row text-2">
                              {land.ln_total_sqft_area} sqft
                              <br />
                              Plot Area
                            </div>
                            <div className="plot-details-row text-2">
                              {land.ln_plottype}
                            </div>
                            <div className="plot-details-row text-2 no-border">
                              {new Date(land.ln_post_date).toLocaleDateString(
                                "en-GB",
                                {
                                  day: "numeric",
                                  month: "long",
                                  year: "numeric",
                                }
                              )}
                              <br />
                              Posted on
                            </div>
                          </div>
                          <div className="image-wrap land-img">
                            <Link to={`/authuser/View-Land/${land.ln_id}`}>
                              <img
                                className="ls-is-cached lazyloaded"
                                src={
                                  land.image_files &&
                                  land.image_files.length > 0
                                    ? `${API_BASE}/Uploads/lands/${land.image_files[0]}`
                                    : `${process.env.REACT_APP_STATIC_BASE_URL}/frontend/images/No-Image-Placeholder.jpg`
                                }
                                alt="Plot"
                              />
                            </Link>
                          </div>
                          <div className="content">
                            <h4 className="text_white">
                              <i className="bi bi-currency-rupee me-1"></i>
                              {formatShortPrice(land.ln_price)} <br />
                              <small className="text-1">
                                <i className="bi bi-currency-rupee me-1"></i>
                                {formatNumberIndian(
                                  land.ln_total_area_sqft
                                )}{" "}
                                per sqft
                              </small>
                            </h4>
                          </div>
                          <div className="form-check sold">
                            <input
                              className="form-check-input location-checkbox"
                              type="checkbox"
                              id={`soldout-${land.ln_id}`}
                              checked={land.ln_availability === "Sold"}
                              onChange={() =>
                                updateLandAvailability(
                                  land.ln_id,
                                  land.ln_availability || "Avail"
                                )
                              }
                              disabled={updatingAvailability === land.ln_id}
                            />
                            <label
                              className="form-check-label"
                              htmlFor={`soldout-${land.ln_id}`}
                            >
                              <span className="text-4">
                                {updatingAvailability === land.ln_id
                                  ? "Updating..."
                                  : "Sold out"}
                              </span>
                            </label>
                          </div>
                          <Link
                            to={`/authuser/View-Land/${land.ln_id}`}
                            className="tf-btn style-border view-more text-3"
                          >
                            View Details
                            <i className="bi bi-arrow-right-circle-fill ms-2"></i>
                          </Link>
                          <Link
                            to={`/authuser/Edit-Land/${land.ln_id}`}
                            className="btn-edit"
                          >
                            <i className="bi bi-pencil"></i>
                          </Link>
                        </div>
                        <div className="count-box border">
                          <table className="table">
                            <tbody>
                              <tr>
                                <td className="text-center">
                                  <span>{land.view_count || 0}</span>
                                  <br />
                                  Views
                                </td>
                                <td className="text-center">
                                  <span>{land.wishlist_count || 0}</span>
                                  <br />
                                  Favourites
                                </td>
                                <td className="text-center">
                                  <span>{land.enquiry_count || 0}</span>
                                  <br />
                                  <Link to="/authuser/enquiries">
                                    Enquiries
                                  </Link>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="col-lg-12">
                      <NoResults description="Try adjusting your search or filter to find what you're looking for." />
                    </div>
                  )}
                </div>

                {/* New Pagination Design */}
                {pagination && pagination.totalPages > 1 && (
                  <div className="d-flex justify-content-center align-items-center mt-4">
                    <ul className="pagination justify-content-center circle">
                      <li
                        className={`page-item ${
                          currentPage === 1 ? "disabled" : ""
                        }`}
                      >
                        <a
                          className="page-link"
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            if (currentPage > 1)
                              handlePageChange(currentPage - 1);
                          }}
                        >
                          «
                        </a>
                      </li>

                      {renderPaginationItems()}

                      <li
                        className={`page-item ${
                          currentPage === pagination.totalPages
                            ? "disabled"
                            : ""
                        }`}
                      >
                        <a
                          className="page-link"
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            if (currentPage < pagination.totalPages)
                              handlePageChange(currentPage + 1);
                          }}
                        >
                          »
                        </a>
                      </li>
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </FrontendLayout>
  );
};

export default MyLands;
